import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Select } from '@xsolla/ui-core';
import { Search } from '@xsolla/ui-icons';

import { SocialConnection, SocialConnectionsResponse } from '../../../api';
import { Preloader } from '../../ui';
import { FieldNames } from '../SocialSettingsMainBox/types';
import { SelectConnectionMode } from './SelectConnectionMode';
import SocialNetworkCard from './SocialNetworkCard';
import {
  CardWrapper,
  RigthContentWrapper,
  SearchBarWrapper,
  SelectButton,
  SocialModeSelectWrapper,
  SocialSearchInput
} from './style';

type Props = {
  projectId: string;
  currentSocials: SocialConnectionsResponse | null;
  currentSocialsFetching: boolean;
  setCurrentSocials: Dispatch<SetStateAction<SocialConnectionsResponse>>;
};

enum SocialSelectTypes {
  all = 'all',
  connected = 'connected',
  disconnected = 'disconnected'
}

export type ChosenSocials = {
  [key: string]: SocialConnection | null;
};

const SocialsList = ({
  projectId,
  currentSocials,
  currentSocialsFetching,
  setCurrentSocials
}: Props): JSX.Element => {
  const intl = useIntl();
  const [filteredSocials, setFilteredSocials] = useState<SocialConnectionsResponse | null>();
  const [fetchDataMode, setFetchDataMode] = useState<boolean>(true);
  const [chosenSocials, setChosenSocials] = useState<ChosenSocials | null>({});
  const [connectionsMode, setConnectionsMode] = useState<SocialSelectTypes>(SocialSelectTypes.all);

  const isCardSelected = useMemo(() => {
    return !!chosenSocials && Object.values(chosenSocials).filter((item) => item).length > 0;
  }, [chosenSocials]);

  useEffect(() => {
    if (!currentSocials) {
      return;
    }
    if (fetchDataMode) {
      const nonCustomSocial = currentSocials.filter((obj) => !obj.is_custom);
      setFilteredSocials(nonCustomSocial);
      setFetchDataMode(false);
    }
  }, [currentSocials]);

  const selectSocialOptions = [
    {
      children: intl.messages['social-connection.select.all'],
      value: SocialSelectTypes.all,
      'data-test-id': 'filter-all'
    },
    {
      children: intl.messages['social-connection.select.connected'],
      value: SocialSelectTypes.connected,
      'data-test-id': 'filter-connected'
    },
    {
      children: intl.messages['social-connection.select.disconnected'],
      value: SocialSelectTypes.disconnected,
      'data-test-id': 'filter-disconnected'
    }
  ];

  const handleSelectMode = useCallback(
    (e) => {
      setConnectionsMode(e);
      setChosenSocials({});
      switch (e) {
        case SocialSelectTypes.connected:
          setFilteredSocials(currentSocials?.filter((item) => item.connected));
          return;
        case SocialSelectTypes.disconnected:
          setFilteredSocials(currentSocials?.filter((item) => !item.connected));
          return;
        default:
          setFilteredSocials(currentSocials);
      }
    },
    [filteredSocials]
  );

  const deselectAllHandle = () => setChosenSocials({});

  const selectAllHandle = useCallback(() => {
    if (!filteredSocials) {
      return;
    }
    const newChosenSocials = filteredSocials
      .filter((social) =>
        social.configurable_fields.some(
          (field) => field.name !== FieldNames.CUSTOM_RETURN_URL && !field.required
        )
      )
      .reduce((acc, item) => ({ ...acc, [item.provider]: item }), {});
    setChosenSocials(newChosenSocials);
  }, [filteredSocials]);

  const findSocialHandle = useCallback(
    (e) => {
      if (!currentSocials) {
        return;
      }
      const foundSocials = currentSocials.filter((item) => {
        const fullSocialName = intl.formatMessage({
          id: `auth-methods.${item.provider}`
        });

        return fullSocialName.toLowerCase().includes(e.target.value.toLowerCase());
      });

      setFilteredSocials(foundSocials);
    },
    [currentSocials]
  );

  return (
    <>
      <SearchBarWrapper>
        <RigthContentWrapper>
          <SelectButton
            data-test-id="social-select-btn"
            onClick={isCardSelected ? deselectAllHandle : selectAllHandle}
            look="link"
            palette="brand"
            size="md">
            {intl.messages[`social-connection.select.${isCardSelected ? 'deselect' : 'select'}`]}
          </SelectButton>
          <div>
            <SocialModeSelectWrapper>
              <Select
                data-test-id="social-connect-filter"
                onChange={handleSelectMode}
                options={selectSocialOptions}
                placeholder={SocialSelectTypes.all}
                size="sm"
                value={connectionsMode}
              />
            </SocialModeSelectWrapper>
          </div>
          <SocialSearchInput
            data-test-id="social-search"
            extraAlign="left"
            onChange={findSocialHandle}
            placeholder={intl.formatMessage({ id: 'social-connection.search.placeholder' })}
            extra={Search}
            uiSize="sm"
          />
        </RigthContentWrapper>
        {chosenSocials && (
          <SelectConnectionMode
            setSocials={setCurrentSocials}
            setFilteredSocials={setFilteredSocials}
            setChosenSocials={setChosenSocials}
            projectId={projectId}
            socialsList={chosenSocials}
          />
        )}
      </SearchBarWrapper>
      <CardWrapper isFetching={currentSocialsFetching} data-test-id="social-list">
        {currentSocialsFetching && <Preloader />}
        {filteredSocials?.map((item) => {
          return (
            <SocialNetworkCard
              projectId={projectId}
              socialData={item}
              chosen={!!chosenSocials?.[item.provider]}
              setSocials={setCurrentSocials}
              setFilteredSocials={setFilteredSocials}
              setChosenSocials={setChosenSocials}
              key={item.provider}
            />
          );
        })}
      </CardWrapper>
    </>
  );
};

export default SocialsList;
